import { useEffect, useMemo, useState } from "react";

import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { CheckCircle2, Loader2, Paperclip, X } from "lucide-react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { isApCreditNote } from "~/constants/siiDocTypes";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import {
  useActiveSuppliers,
  useCreateManualSupplierInvoice,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { formatCurrency } from "~/utils/helpers";

// Public document store (same bucket AdjuntarPdfDialog uses) — the uploaded PDF becomes a
// permanent public URL stored in manual_pdf_url.
const BUCKET = "sales-request-files";
const MAX_MB = 5;

const TIPO_OPTIONS = [
  { value: "FE", label: "Factura" },
  { value: "FNE", label: "Factura Exenta" },
  { value: "FEX", label: "Factura Extranjera" },
  { value: "DI", label: "Declaración de Ingreso (DI 914)" },
  { value: "NC", label: "Nota de Crédito" },
  { value: "NCEX", label: "Nota de Crédito extranjera" },
] as const;

const schema = z.object({
  companyId: z.string().min(1, "Selecciona la empresa"),
  supplierId: z.string().min(1, "Selecciona el proveedor"),
  docTipoCode: z.enum(["FE", "FNE", "FEX", "DI", "NC", "NCEX"]),
  folio: z.string().trim().min(1, "Ingresa el folio"),
  emissionDate: z.string().min(1, "Ingresa la fecha de emisión"),
  dueDate: z.string().optional(),
  neto: z.coerce.number().min(0, "No puede ser negativo"),
  iva: z.coerce.number().min(0, "No puede ser negativo"),
  exento: z.coerce.number().min(0, "No puede ser negativo"),
  imptoEspecifico: z.coerce.number().min(0, "No puede ser negativo"),
  glosa: z.string().optional(),
});

type ManualInvoiceFormData = z.infer<typeof schema>;

const defaultValues: ManualInvoiceFormData = {
  companyId: ALIACE_SPA_ID,
  supplierId: "",
  docTipoCode: "FE",
  folio: "",
  emissionDate: dayjs().format("YYYY-MM-DD"),
  dueDate: "",
  neto: 0,
  iva: 0,
  exento: 0,
  imptoEspecifico: 0,
  glosa: "",
};

const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

interface SupplierInvoiceManualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupplierInvoiceManualDialog({
  open,
  onOpenChange,
}: SupplierInvoiceManualDialogProps) {
  const { toast } = useToast();
  const { data: companies = [] } = useCompanies();
  const { data: suppliers = [] } = useActiveSuppliers();
  const createManual = useCreateManualSupplierInvoice();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ManualInvoiceFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
      setFile(null);
    }
  }, [open, reset]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: s.id,
        label: s.name,
        description: s.rut ?? undefined,
        searchTerms: `${s.name} ${s.rut ?? ""}`,
      })),
    [suppliers]
  );

  const [neto, iva, exento, impto, docTipoCode] = watch([
    "neto",
    "iva",
    "exento",
    "imptoEspecifico",
    "docTipoCode",
  ]);
  const totalMagnitude =
    num(neto) + num(iva) + num(exento) + num(impto);
  const isNC = isApCreditNote(docTipoCode);
  const busy = uploading || createManual.isPending;

  const onSubmit = async (values: ManualInvoiceFormData) => {
    if (totalMagnitude <= 0) {
      toast({
        title: "Monto inválido",
        description: "El total debe ser mayor a 0.",
        variant: "destructive",
      });
      return;
    }

    // Optional PDF: upload first, then pass the public URL as manual_pdf_url.
    let manualPdfUrl: string | null = null;
    if (file) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Archivo inválido",
          description: "El documento debe ser un PDF.",
          variant: "destructive",
        });
        return;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast({
          title: "Archivo muy grande",
          description: `El PDF supera el máximo de ${MAX_MB} MB.`,
          variant: "destructive",
        });
        return;
      }
      setUploading(true);
      try {
        const safe = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
        const path = `supplier-invoices/manual/${Date.now()}_${safe}`;
        const { error: upErr } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: "application/pdf",
          });
        if (upErr) throw new Error(upErr.message);
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
        if (!data?.publicUrl)
          throw new Error("No se pudo obtener la URL del archivo.");
        manualPdfUrl = data.publicUrl;
      } catch (e) {
        setUploading(false);
        toast({
          title: "Error al subir el PDF",
          description: (e as Error).message,
          variant: "destructive",
        });
        return;
      }
      setUploading(false);
    }

    // AP sign convention: documentos por pagar (FE/FNE/FEX/DI) negative, notas de crédito
    // (NC/NCEX) positive ("en el haber").
    const sign = isApCreditNote(values.docTipoCode) ? 1 : -1;
    const hasDue = !!values.dueDate;

    createManual.mutate(
      {
        supplierId: values.supplierId,
        companyId: values.companyId,
        docTipoCode: values.docTipoCode,
        documentType: null,
        folio: values.folio.trim(),
        emissionDate: values.emissionDate,
        dueDate: hasDue
          ? values.dueDate
          : dayjs(values.emissionDate).add(30, "day").format("YYYY-MM-DD"),
        dueDateEstimated: !hasDue,
        exento: sign * values.exento,
        neto: sign * values.neto,
        iva: sign * values.iva,
        imptoEspecifico: sign * values.imptoEspecifico,
        totalClp: sign * totalMagnitude,
        saldoClp: sign * totalMagnitude,
        glosa: values.glosa?.trim() || null,
        manualPdfUrl,
      },
      {
        onSuccess: () => {
          toast({
            title: "Factura registrada",
            description: `${TIPO_OPTIONS.find((t) => t.value === values.docTipoCode)?.label} ${values.folio} cargada manualmente.`,
          });
          onOpenChange(false);
        },
        onError: (e) =>
          toast({
            title: "No se pudo registrar",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Cargar factura manualmente</DialogTitle>
          <DialogDescription>
            Registra una factura de proveedor que no vino en el export de BSale.
            Los montos se ingresan en positivo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Empresa + proveedor */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="manual-empresa">Empresa</Label>
              <Controller
                control={control}
                name="companyId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="manual-empresa" className="h-9">
                      <SelectValue placeholder="Selecciona la empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Tipo de documento</Label>
              <Controller
                control={control}
                name="docTipoCode"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPO_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Proveedor</Label>
            <Controller
              control={control}
              name="supplierId"
              render={({ field }) => (
                <SearchableSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={supplierOptions}
                  placeholder="Busca por nombre o RUT…"
                  searchPlaceholder="Nombre o RUT…"
                  emptyMessage="Sin proveedores"
                />
              )}
            />
            {errors.supplierId && (
              <p className="text-xs text-red-600">{errors.supplierId.message}</p>
            )}
          </div>

          {/* Folio + fechas */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="manual-folio">Folio</Label>
              <Input
                id="manual-folio"
                className="h-9"
                placeholder="N° documento"
                {...register("folio")}
              />
              {errors.folio && (
                <p className="text-xs text-red-600">{errors.folio.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-emision">Fecha de emisión</Label>
              <Input
                id="manual-emision"
                type="date"
                className="h-9"
                {...register("emissionDate")}
              />
              {errors.emissionDate && (
                <p className="text-xs text-red-600">
                  {errors.emissionDate.message}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-vencimiento">Vencimiento</Label>
              <Input
                id="manual-vencimiento"
                type="date"
                className="h-9"
                {...register("dueDate")}
              />
              <p className="text-xs text-muted-foreground">
                Vacío = +30 días (est.)
              </p>
            </div>
          </div>

          {/* Montos */}
          <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="manual-neto">Neto afecto</Label>
                <Input
                  id="manual-neto"
                  type="number"
                  min={0}
                  step={1}
                  className="h-9 text-right"
                  {...register("neto")}
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="manual-iva">IVA</Label>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline disabled:opacity-50"
                    disabled={num(neto) <= 0}
                    onClick={() =>
                      setValue("iva", Math.round(num(neto) * 0.19), {
                        shouldValidate: true,
                      })
                    }
                  >
                    Calcular 19%
                  </button>
                </div>
                <Input
                  id="manual-iva"
                  type="number"
                  min={0}
                  step={1}
                  className="h-9 text-right"
                  {...register("iva")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-exento">Exento</Label>
                <Input
                  id="manual-exento"
                  type="number"
                  min={0}
                  step={1}
                  className="h-9 text-right"
                  {...register("exento")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="manual-impto">Impuesto específico</Label>
                <Input
                  id="manual-impto"
                  type="number"
                  min={0}
                  step={1}
                  className="h-9 text-right"
                  {...register("imptoEspecifico")}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-3">
              <span className="text-sm font-medium text-gray-700">
                Total {isNC ? "(nota de crédito)" : ""}
              </span>
              <span
                className={`text-lg font-bold ${isNC ? "text-green-700" : "text-gray-900"}`}
              >
                ${formatCurrency(totalMagnitude)}
              </span>
            </div>
          </div>

          {/* Glosa */}
          <div className="space-y-1.5">
            <Label htmlFor="manual-glosa">Glosa (descripción)</Label>
            <Textarea
              id="manual-glosa"
              rows={2}
              placeholder="Descripción del documento (opcional)…"
              {...register("glosa")}
            />
          </div>

          {/* PDF opcional */}
          <div className="space-y-1.5">
            <Label>Documento (PDF, opcional)</Label>
            {!file ? (
              <Input
                type="file"
                accept="application/pdf"
                className="h-9"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            ) : (
              <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Puedes adjuntarlo ahora o más tarde desde la columna Documento.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={busy || totalMagnitude <= 0}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando…
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Registrar factura
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
